import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { archivalApi } from '@xtnd/web/features/archival/api';
import { showToast } from '@repo/ui';
import type {
  ArchivalConfiguration,
  ArchivalServiceInfo,
  CreateArchivalConfigDto,
} from '@xtnd/web/features/archival/types/archivalType';

// Query Keys
export const archivalKeys = {
  all: ['archival'] as const,
  configurations: ['archival-configurations'] as const,
  services: ['archival-services'] as const,
  configurationStatus: ['configuration-status'] as const,
  test: (configId: string) => ['test-config', configId] as const,
};

/**
 * Hook to fetch archival configurations
 * Automatically refreshes every 30 seconds
 */
export function useArchivalConfigurations() {
  return useQuery<ArchivalConfiguration[]>({
    queryKey: archivalKeys.configurations,
    queryFn: () => archivalApi.getConfigurations(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook to fetch supported archival services
 */
export function useSupportedServices() {
  return useQuery<ArchivalServiceInfo[]>({
    queryKey: archivalKeys.services,
    queryFn: () => archivalApi.getSupportedServices(),
  });
}

/**
 * Hook to create a new archival configuration
 */
export function useCreateConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateArchivalConfigDto) => archivalApi.createConfiguration(data),
    onSuccess: () => {
      showToast.success('Configuration created successfully');
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurations });
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurationStatus });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to create configuration: ${error.message}`);
    },
  });
}

/**
 * Hook to update an existing archival configuration
 */
export function useUpdateConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      serviceId,
      data,
    }: {
      tenantId: string;
      serviceId: string;
      data: CreateArchivalConfigDto;
    }) => archivalApi.updateConfiguration(tenantId, serviceId, data),
    onSuccess: () => {
      showToast.success('Configuration updated successfully');
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurations });
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurationStatus });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update configuration: ${error.message}`);
    },
  });
}

/**
 * Hook to delete an archival configuration
 */
export function useDeleteConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, serviceId }: { tenantId: string; serviceId: string }) =>
      archivalApi.deleteConfiguration(tenantId, serviceId),
    onSuccess: () => {
      showToast.success('Configuration deleted successfully');
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurations });
      queryClient.invalidateQueries({ queryKey: archivalKeys.configurationStatus });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to delete configuration: ${error.message}`);
    },
  });
}

/**
 * Hook to test an archival configuration
 * Includes 30 second timeout and no retries
 */
export function useTestConfiguration(configId?: string) {
  return useMutation({
    mutationFn: ({ configId }: { configId: string }) =>
      archivalApi.testConfiguration({ configId }),
    onSuccess: result => {
      if (result.success) {
        showToast.success(result.message || 'Configuration test successful!');
      } else {
        showToast.error(result.message || 'Configuration test failed');
      }
    },
    onError: (error: Error) => {
      showToast.error(`Test failed: ${error.message}`);
    },
    mutationKey: configId ? archivalKeys.test(configId) : undefined,
    networkMode: 'online',
    gcTime: 1000 * 60, // 1 minute cache
    retry: false, // Don't retry failed tests
  });
}

/**
 * Combined hook for creating or updating a configuration
 * Automatically determines whether to create or update based on whether config is provided
 */
export function useCreateOrUpdateConfiguration(isEditing: boolean) {
  const createMutation = useCreateConfiguration();
  const updateMutation = useUpdateConfiguration();

  return isEditing ? updateMutation : createMutation;
}

